import { db } from '@/lib/firebase';
import {
  collection,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  limit,
  where,
  Timestamp,
} from 'firebase/firestore';
import type { Video, EntityRef } from '@/types/media';

/** A single credited participant on a video */
export interface VideoContributor {
  name: string;
  /** Linked BB user ID when selected from the user search */
  userId?: string;
  /** Primary role (backwards compat) */
  role: string;
  /** All roles — preferred over single role field */
  roles?: string[];
}

/**
 * Extended Video type with additional fields for BB-Native
 */
export interface BBVideo extends Video {
  isFeatured?: boolean;
  genre?: string;
  /** Multi-genre — first entry mirrors `genre` for backward compat. */
  genres?: string[];
  tags?: string[];
  viewCount?: number;
  status?: 'draft' | 'published' | 'archived';
  youtubeId?: string;
  adminHidden?: boolean;
  /** Full credits — writers, directors, producers, editors, etc. */
  contributors?: VideoContributor[];
  /** Flat email index for contributor lookups */
  contributorEmails?: string[];
  /** Flat uid index so a credited user's profile/media can query
   *  `contributorUserIds array-contains uid` (mirrors works). */
  contributorUserIds?: string[];
  /** Denormalised artist id (from artist.id/docId) for artistId-in queries. */
  artistId?: string;
  copyrightHolder?: string;
  copyrightYear?: number;
  /** Performing-rights org / copyright collecting society (e.g. STIM, SAMI, ASCAP). */
  pro?: string;
  publisher?: string;
  isrc?: string;
  iswc?: string;
  /** Film credit — who directed the video. */
  director?: string;
}

/**
 * Input type for creating a new video
 */
export interface CreateVideoInput {
  title: string;
  description?: string;
  url: string;
  youtubeId: string;
  thumbnailUrl: string;
  durationMs?: number;
  artist: EntityRef;
  genre?: string;
  genres?: string[];
  tags?: string[];
  isFeatured?: boolean;
  creatorId?: string;   // uid of the user who added this video (for self-service delete)
  contributors?: VideoContributor[];
  copyrightHolder?: string;
  copyrightYear?: number;
  pro?: string;
  publisher?: string;
  isrc?: string;
  iswc?: string;
  director?: string;
}

/**
 * Fetch latest videos from Firebase
 */
export async function getLatestVideos(maxItems: number = 10): Promise<BBVideo[]> {
  try {
    const q = query(
      collection(db, 'videos'),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs
      .map(doc => ({ videoId: doc.id, ...doc.data() }) as BBVideo)
      .filter(v => !v.adminHidden);
  } catch (error) {
    console.log('Videos not available yet');
    return [];
  }
}

/**
 * Fetch videos by genre
 */
export async function getVideosByGenre(genre: string, maxItems: number = 10): Promise<BBVideo[]> {
  try {
    const q = query(
      collection(db, 'videos'),
      where('genre', '==', genre),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs
      .map(doc => ({ videoId: doc.id, ...doc.data() }) as BBVideo)
      .filter(v => !v.adminHidden);
  } catch (error) {
    console.error('Error fetching videos by genre:', error);
    return [];
  }
}

/**
 * Fetch the currently featured video for the hero section
 */
export async function getFeaturedVideo(): Promise<BBVideo | null> {
  try {
    // Try featured videos first
    const q = query(
      collection(db, 'videos'),
      where('isFeatured', '==', true),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    const snapshot = await getDocs(q);

    const featuredVisible = snapshot.docs
      .map(d => ({ videoId: d.id, ...d.data() }) as BBVideo)
      .find(v => !v.adminHidden);
    if (featuredVisible) return featuredVisible;

    // Fallback: get the most recent visible video
    const fallbackQ = query(
      collection(db, 'videos'),
      orderBy('createdAt', 'desc'),
      limit(10) // fetch a few so we can skip hidden ones
    );
    const fallbackSnap = await getDocs(fallbackQ);
    const fallbackVisible = fallbackSnap.docs
      .map(d => ({ videoId: d.id, ...d.data() }) as BBVideo)
      .find(v => !v.adminHidden);
    if (fallbackVisible) return fallbackVisible;

    return null;
  } catch (error: any) {
    // If composite index is missing, try without orderBy
    if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
      try {
        const simpleQ = query(collection(db, 'videos'), limit(1));
        const snap = await getDocs(simpleQ);
        if (!snap.empty) {
          return { videoId: snap.docs[0].id, ...snap.docs[0].data() } as BBVideo;
        }
      } catch { /* ignore */ }
    }
    console.log('Featured video not available yet');
    return null;
  }
}

/**
 * Fetch a single video by ID
 */
export async function getVideoById(videoId: string): Promise<BBVideo | null> {
  try {
    const docRef = doc(db, 'videos', videoId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return { videoId: docSnap.id, ...docSnap.data() } as BBVideo;
  } catch (error) {
    console.error('Error fetching video:', error);
    return null;
  }
}

/**
 * Create a new video document
 */
/** Build flat uid/email indexes from a contributors array (mirrors media.ts). */
function buildContributorIndexes(contributors?: VideoContributor[]) {
  const list = Array.isArray(contributors) ? contributors : [];
  const contributorUserIds = [...new Set(list.map(c => c.userId).filter(Boolean))] as string[];
  const contributorEmails = [...new Set(
    list.map(c => (c as any).email).filter(Boolean).map((e: string) => e.toLowerCase())
  )] as string[];
  return { contributorUserIds, contributorEmails };
}

export async function createVideo(input: CreateVideoInput): Promise<string> {
  try {
    const videoRef = doc(collection(db, 'videos'));
    const now = Timestamp.now();
    const { contributorUserIds, contributorEmails } = buildContributorIndexes(input.contributors);
    const artistId = (input.artist as any)?.id || (input.artist as any)?.docId || '';

    await setDoc(videoRef, {
      title: input.title,
      description: input.description || '',
      url: input.url,
      youtubeId: input.youtubeId,
      thumbnailUrl: input.thumbnailUrl,
      durationMs: input.durationMs || 0,
      artist: input.artist,
      ...(artistId ? { artistId } : {}),
      genre: input.genre || (Array.isArray(input.genres) && input.genres[0]) || 'Hip Hop',
      ...(Array.isArray(input.genres) && input.genres.length ? { genres: input.genres } : {}),
      tags: input.tags || [],
      isFeatured: input.isFeatured || false,
      ...(input.creatorId ? { creatorId: input.creatorId } : {}),
      ...(Array.isArray(input.contributors) ? { contributors: input.contributors } : {}),
      contributorUserIds,
      contributorEmails,
      ...(input.copyrightHolder ? { copyrightHolder: input.copyrightHolder } : {}),
      ...(input.copyrightYear ? { copyrightYear: input.copyrightYear } : {}),
      ...(input.pro ? { pro: input.pro } : {}),
      ...(input.publisher ? { publisher: input.publisher } : {}),
      ...(input.isrc ? { isrc: input.isrc } : {}),
      ...(input.iswc ? { iswc: input.iswc } : {}),
      ...(input.director ? { director: input.director } : {}),
      viewCount: 0,
      status: 'published',
      releaseDate: now,
      createdAt: now,
      updatedAt: now,
    });

    return videoRef.id;
  } catch (error) {
    console.error('Error creating video:', error);
    throw error;
  }
}

/**
 * Update an existing video
 */
export async function updateVideo(videoId: string, updates: Partial<BBVideo>): Promise<void> {
  try {
    const videoRef = doc(db, 'videos', videoId);
    const extra: Record<string, unknown> = {};
    // Keep the flat indexes in sync whenever contributors/artist change.
    if (Array.isArray(updates.contributors)) {
      const { contributorUserIds, contributorEmails } = buildContributorIndexes(updates.contributors);
      extra.contributorUserIds = contributorUserIds;
      extra.contributorEmails = contributorEmails;
    }
    if ((updates as any).artist && !(updates as any).artistId) {
      const aId = (updates as any).artist?.id || (updates as any).artist?.docId || '';
      if (aId) extra.artistId = aId;
    }
    await updateDoc(videoRef, {
      ...updates,
      ...extra,
      updatedAt: Timestamp.now(),
    });
    // See updateWork in services/media.ts for why this event exists — lets
    // FavoritesBar refresh a stale header thumbnail without re-favoriting.
    if ('thumbnailUrl' in updates || 'imageUrl' in updates) {
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('bb:thumbnail-updated', { detail: { itemType: 'video', itemId: videoId } }));
        }
      } catch { /* non-fatal */ }
    }
  } catch (error) {
    console.error('Error updating video:', error);
    throw error;
  }
}

/**
 * Delete a video
 */
export async function deleteVideo(videoId: string): Promise<void> {
  try {
    const videoRef = doc(db, 'videos', videoId);
    await deleteDoc(videoRef);
  } catch (error) {
    console.error('Error deleting video:', error);
    throw error;
  }
}

/**
 * Fetch all unique genres from videos collection
 */
export async function getVideoGenres(): Promise<string[]> {
  try {
    const q = query(collection(db, 'videos'), limit(100));
    const snapshot = await getDocs(q);

    const genres = new Set<string>();
    snapshot.docs.forEach(doc => {
      const genre = doc.data().genre;
      if (genre) genres.add(genre);
    });

    return Array.from(genres).sort();
  } catch (error) {
    console.log('Video genres not available');
    return [];
  }
}
